import { useEffect, useState } from "react";
import { useTheme } from "styled-components";
import { useSetRecoilState } from "recoil";

import { useUser } from "MVVM/Hooks/useUser";
import { useTrialCardStatus } from "MVVM/Hooks/useTrialCardStatus";
import { StripePaystationState } from "MVVM/State";
import { analytics } from "MVVM/Analytics";
import { NoticeBanner } from "../NoticeBanner";

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * Countdown for no-card trial users: shown only while a trialing subscription
 * has no payment method attached. Card-on-file trials already auto-convert, so
 * they never see this. Dismissal is keyed per remaining-day bucket, so the
 * banner returns each day the trial gets shorter.
 */
const TrialCountdownBanner = () => {
  const { allstarUser } = useUser();
  const { status } = useTrialCardStatus();
  const theme = useTheme() as any;
  const setStripePaystation = useSetRecoilState(StripePaystationState);

  const expires = allstarUser?.user?.userGroup?.expires;
  const daysLeft = expires
    ? Math.max(0, Math.ceil((Number(expires) - Date.now()) / DAY_MS))
    : 0;

  const hash = `trial-countdown-${daysLeft}d`;
  const dismissKey = `banner-announcement-${hash}-has-been-closed`;
  const [shouldShow, setShouldShow] = useState(true);

  const visible =
    shouldShow &&
    !localStorage.getItem(dismissKey) &&
    !!allstarUser?.user?.userGroup?.isTrialPeriod &&
    daysLeft > 0 &&
    !!status?.trialing &&
    !status.hasCard;

  useEffect(() => {
    if (visible)
      analytics.track("Trial Countdown Banner - Viewed", { daysLeft });
  }, [visible, daysLeft]);

  if (!visible) return <></>;

  const onDismiss = (show: boolean) => {
    if (!show)
      analytics.track("Trial Countdown Banner - Dismissed", { daysLeft });
    setShouldShow(show);
  };

  const onClick = () => {
    analytics.track("Trial Countdown Banner - Add Card Clicked", { daysLeft });
    setStripePaystation({
      isModalOpen: true,
      type: "subscribe",
      pendingEmail: false,
      shouldRefreshMtx: false,
      trialCardSetup: true,
    });
  };

  const dayWord = daysLeft === 1 ? "day" : "days";

  return (
    <NoticeBanner
      hash={hash}
      backgroundColor={theme.colors.envy}
      display="flex"
      setShouldShow={onDismiss}
      onClick={onClick}
    >
      {daysLeft === 1
        ? "Last day of your free trial!"
        : `${daysLeft} ${dayWord} left in your free trial.`}{" "}
      {status?.extended
        ? "Add a card to keep Unlimited."
        : "Add a card to keep Unlimited and get 7 bonus trial days."}
    </NoticeBanner>
  );
};

export { TrialCountdownBanner };
